import type { Lang } from '../types';

/**
 * Strings for the grouped navigation of the apartment edit page (Issue #137): the five group tabs,
 * the setup checklist, the settings search and the save bar. Kept apart from `apartmentDetail.ts` on
 * purpose — the labels of the fields themselves still come from there, so a renamed field renames
 * its search result too.
 */
export interface ApartmentEditNavText {
  // Group tabs
  tabsAria: string;
  groupBasic: string;
  groupReservations: string;
  groupArrival: string;
  groupMoney: string;
  groupAuthorities: string;
  /** One-word versions for the phone's single row of five. */
  shortBasic: string;
  shortReservations: string;
  shortArrival: string;
  shortMoney: string;
  shortAuthorities: string;
  tabNeedsAttention: string;
  tabUnsaved: string;

  // Checklist
  checklistRegion: string;
  checklistProgress: (done: number, total: number) => string;
  checklistAllDone: string;
  checklistLoading: string;
  checklistShow: string;
  checklistHide: string;
  checklistAriaProgress: (done: number, total: number) => string;
  checklistAriaAllDone: string;
  checklistAriaLoading: string;
  checklistHint: string;
  statusDone: string;
  statusTodo: string;
  statusNa: string;
  ckCalendar: string;
  ckUbyportLogin: string;
  ckUbyportAuto: string;
  ckKeyAccess: string;
  ckWifi: string;
  ckGuideEnabled: string;
  ckStripe: string;

  // Search
  searchLabel: string;
  searchPlaceholderKeyboard: string;
  searchPlaceholderTouch: string;
  searchResults: string;
  searchEmpty: string;

  // Save bar
  barRegion: string;
  barUnsaved: (groups: string) => string;
  barClean: string;
  barDiscard: string;
  barSave: string;
  barGuideSaving: string;

  // Basic group
  dangerZone: string;
  checkinLinkTitle: string;

  // Collapsible cards and "advanced" blocks (Variant 4 of #132)
  advanced: string;
  advancedLock: string;
  chipOn: string;
  chipOff: string;
  chipOnDemand: string;
  chipOnline: string;
  cardUnsaved: string;
  /** `rate` is already formatted ("50" or "—"). */
  sumCityTax: (rate: string, online: boolean) => string;
  /** `enabled` is undefined while the setting is still loading. `amount` is already formatted. */
  sumDeposit: (enabled: boolean | null | undefined, amount: string | null) => string;
  /** Modes are OFF / AUTO / REQUEST; undefined while loading. */
  sumUpsell: (hours: string | null | undefined, luggage: string | null | undefined) => string;
  sumDorucenky: string;
  sumExport: string;

  // Guide: five groups
  guideHelpOn: string;
  guideHelpOff: string;
  ggArrival: string;
  ggAccess: string;
  ggStay: string;
  ggContent: string;
  ggDeparture: string;
  gsHidden: string;
  gsNoAddress: string;
  gsArrDep: (address: string, checkIn: string, checkOut: string) => string;
  gsCodeMissing: string;
  gsLockMissing: string;
  gsWifiMissing: string;
  gsNothingShown: string;
  gsOptional: string;
  gsDeparture: (time: string, filled: boolean) => string;
}

export const apartmentEditNavText: Record<Lang, ApartmentEditNavText> = {
  cs: {
    tabsAria: 'Skupiny nastavení',
    groupBasic: 'Základ',
    groupReservations: 'Rezervace',
    groupArrival: 'Příjezd a průvodce',
    groupMoney: 'Peníze',
    groupAuthorities: 'Úřady a export',
    shortBasic: 'Základ',
    shortReservations: 'Rezervace',
    shortArrival: 'Příjezd',
    shortMoney: 'Peníze',
    shortAuthorities: 'Úřady',
    tabNeedsAttention: 'vyžaduje pozornost',
    tabUnsaved: 'neuložené změny',

    checklistRegion: 'Nastavení apartmánu',
    checklistProgress: (d, t) => `Nastavení: ${d} ze ${t} hotovo`,
    checklistAllDone: 'Vše nastaveno',
    checklistLoading: 'Načítám stav nastavení…',
    checklistShow: 'Zobrazit',
    checklistHide: 'Skrýt',
    checklistAriaProgress: (d, t) => `Nastavení: ${d} ze ${t} hotovo`,
    checklistAriaAllDone: 'Nastavení: vše hotovo',
    checklistAriaLoading: 'Nastavení: načítám',
    checklistHint: 'Seznam se odvozuje z uložených hodnot — neuložená změna se započítá až po Uložit.',
    statusDone: 'Hotovo',
    statusTodo: 'Doplnit ›',
    statusNa: 'Není potřeba',
    ckCalendar: 'Kalendář propojen',
    ckUbyportLogin: 'Ubyport přihlášení vyplněno',
    ckUbyportAuto: 'Automatické odesílání do Ubyportu',
    ckKeyAccess: 'Přístup ke klíčům nastaven',
    ckWifi: 'Wi-Fi vyplněno',
    ckGuideEnabled: 'Průvodce zapnut',
    ckStripe: 'Stripe účet',

    searchLabel: 'Najít nastavení',
    searchPlaceholderKeyboard: 'Najít nastavení… (stiskněte / )',
    searchPlaceholderTouch: 'Najít nastavení… např. wifi, kauce',
    searchResults: 'Výsledky hledání',
    searchEmpty: 'Nic nenalezeno — zkuste jiné slovo',

    barRegion: 'Uložení změn',
    barUnsaved: (g) => `Neuložené změny: ${g}`,
    barClean: 'Žádné neuložené změny',
    barDiscard: 'Zahodit',
    barSave: 'Uložit',
    barGuideSaving: 'Ukládám průvodce…',

    dangerZone: 'Nebezpečná zóna',
    checkinLinkTitle: 'Check-in hostů',

    advanced: 'Pokročilé nastavení',
    advancedLock: 'Pokročilé: provozní akce zámku',
    chipOn: 'Zapnuto',
    chipOff: 'Vypnuto',
    chipOnDemand: 'Na vyžádání',
    chipOnline: 'Online výběr',
    cardUnsaved: 'Neuložené změny',
    sumCityTax: (r, o) => `${r} Kč / dospělý / noc · online výběr ${o ? 'zapnut' : 'vypnut'}`,
    sumDeposit: (e, a) => (e == null ? '…' : e ? (a ? `${a} · rezervuje se na kartě před příjezdem` : 'Zapnuto') : 'Vypnuto'),
    sumUpsell: (h, l) => {
      if (h === undefined || l === undefined) return '…';
      const name = (m: string) => (m === 'AUTO' ? 'automaticky' : 'na žádost');
      const parts = [h && h !== 'OFF' ? `Hodiny navíc: ${name(h)}` : null, l && l !== 'OFF' ? `Zavazadla: ${name(l)}` : null].filter(Boolean);
      return parts.length ? parts.join(' · ') : 'Vypnuto';
    },
    sumDorucenky: 'ZIP s PDF doručenkami za zvolené období',
    sumExport: 'CSV a PDF za zvolené období',

    guideHelpOn: 'Obsah je připravený pro hosty.',
    guideHelpOff: 'Obsah zůstává uložený, hostům se nezobrazuje.',
    ggArrival: 'Příjezd a adresa',
    ggAccess: 'Přístup a klíče',
    ggStay: 'Během pobytu',
    ggContent: 'Pokyny, fotky a odkazy',
    ggDeparture: 'Odjezd',
    gsHidden: 'Skryto hostům',
    gsNoAddress: 'Adresa není vyplněná',
    gsArrDep: (a, i, o) => `${a} · příjezd ${i || '—'} / odjezd ${o || '—'}`,
    gsCodeMissing: 'kód chybí',
    gsLockMissing: 'zámek nevybrán',
    gsWifiMissing: 'Wi-Fi chybí',
    gsNothingShown: 'Vše skryto hostům',
    gsOptional: 'Volitelné informace · přidat podle potřeby',
    gsDeparture: (time, f) => `Odjezd do ${time || '—'} · ${f ? 'pokyny vyplněny' : 'přidat pokyny'}`,
  },
  en: {
    tabsAria: 'Setting groups',
    groupBasic: 'Basics',
    groupReservations: 'Reservations',
    groupArrival: 'Arrival & guide',
    groupMoney: 'Money',
    groupAuthorities: 'Authorities & export',
    shortBasic: 'Basics',
    shortReservations: 'Bookings',
    shortArrival: 'Arrival',
    shortMoney: 'Money',
    shortAuthorities: 'Official',
    tabNeedsAttention: 'needs attention',
    tabUnsaved: 'unsaved changes',

    checklistRegion: 'Apartment setup',
    checklistProgress: (d, t) => `Setup: ${d} of ${t} done`,
    checklistAllDone: 'Everything is set up',
    checklistLoading: 'Loading setup status…',
    checklistShow: 'Show',
    checklistHide: 'Hide',
    checklistAriaProgress: (d, t) => `Setup: ${d} of ${t} done`,
    checklistAriaAllDone: 'Setup: all done',
    checklistAriaLoading: 'Setup: loading',
    checklistHint: 'This list is based on saved values — an unsaved change counts only after you press Save.',
    statusDone: 'Done',
    statusTodo: 'Complete ›',
    statusNa: 'Not needed',
    ckCalendar: 'Calendar connected',
    ckUbyportLogin: 'Ubyport login filled in',
    ckUbyportAuto: 'Automatic submission to Ubyport',
    ckKeyAccess: 'Key access set up',
    ckWifi: 'Wi-Fi filled in',
    ckGuideEnabled: 'Guide switched on',
    ckStripe: 'Stripe account',

    searchLabel: 'Find a setting',
    searchPlaceholderKeyboard: 'Find a setting… (press / )',
    searchPlaceholderTouch: 'Find a setting… e.g. wifi, deposit',
    searchResults: 'Search results',
    searchEmpty: 'Nothing found — try another word',

    barRegion: 'Save changes',
    barUnsaved: (g) => `Unsaved changes: ${g}`,
    barClean: 'No unsaved changes',
    barDiscard: 'Discard',
    barSave: 'Save',
    barGuideSaving: 'Saving the guide…',

    dangerZone: 'Danger zone',
    checkinLinkTitle: 'Guest check-in',

    advanced: 'Advanced settings',
    advancedLock: 'Advanced: lock operations',
    chipOn: 'On',
    chipOff: 'Off',
    chipOnDemand: 'On request',
    chipOnline: 'Online collection',
    cardUnsaved: 'Unsaved changes',
    sumCityTax: (r, o) => `${r} CZK / adult / night · online collection ${o ? 'on' : 'off'}`,
    sumDeposit: (e, a) => (e == null ? '…' : e ? (a ? `${a} · held on the card before arrival` : 'On') : 'Off'),
    sumUpsell: (h, l) => {
      if (h === undefined || l === undefined) return '…';
      const name = (m: string) => (m === 'AUTO' ? 'automatic' : 'on request');
      const parts = [h && h !== 'OFF' ? `Extra hours: ${name(h)}` : null, l && l !== 'OFF' ? `Luggage: ${name(l)}` : null].filter(Boolean);
      return parts.length ? parts.join(' · ') : 'Off';
    },
    sumDorucenky: 'ZIP of PDF receipts for the chosen period',
    sumExport: 'CSV and PDF for the chosen period',

    guideHelpOn: 'The content is ready for guests.',
    guideHelpOff: 'The content stays saved but guests do not see it.',
    ggArrival: 'Arrival & address',
    ggAccess: 'Access & keys',
    ggStay: 'During the stay',
    ggContent: 'Instructions, photos & links',
    ggDeparture: 'Departure',
    gsHidden: 'Hidden from guests',
    gsNoAddress: 'No address filled in',
    gsArrDep: (a, i, o) => `${a} · arrival ${i || '—'} / departure ${o || '—'}`,
    gsCodeMissing: 'code missing',
    gsLockMissing: 'no lock selected',
    gsWifiMissing: 'Wi-Fi missing',
    gsNothingShown: 'Everything hidden from guests',
    gsOptional: 'Optional information · add as needed',
    gsDeparture: (time, f) => `Check-out by ${time || '—'} · ${f ? 'instructions filled in' : 'add instructions'}`,
  },
  vi: {
    tabsAria: 'Nhóm cài đặt',
    groupBasic: 'Cơ bản',
    groupReservations: 'Đặt phòng',
    groupArrival: 'Đến nơi & hướng dẫn',
    groupMoney: 'Tiền',
    groupAuthorities: 'Cơ quan & xuất dữ liệu',
    shortBasic: 'Cơ bản',
    shortReservations: 'Đặt phòng',
    shortArrival: 'Đến nơi',
    shortMoney: 'Tiền',
    shortAuthorities: 'Cơ quan',
    tabNeedsAttention: 'cần chú ý',
    tabUnsaved: 'có thay đổi chưa lưu',

    checklistRegion: 'Thiết lập căn hộ',
    checklistProgress: (d, t) => `Thiết lập: ${d}/${t} hoàn tất`,
    checklistAllDone: 'Đã thiết lập xong',
    checklistLoading: 'Đang tải trạng thái thiết lập…',
    checklistShow: 'Hiện',
    checklistHide: 'Ẩn',
    checklistAriaProgress: (d, t) => `Thiết lập: ${d}/${t} hoàn tất`,
    checklistAriaAllDone: 'Thiết lập: đã xong tất cả',
    checklistAriaLoading: 'Thiết lập: đang tải',
    checklistHint: 'Danh sách dựa trên giá trị đã lưu — thay đổi chưa lưu chỉ được tính sau khi bạn bấm Lưu.',
    statusDone: 'Xong',
    statusTodo: 'Bổ sung ›',
    statusNa: 'Không cần',
    ckCalendar: 'Đã kết nối lịch',
    ckUbyportLogin: 'Đã nhập đăng nhập Ubyport',
    ckUbyportAuto: 'Tự động gửi lên Ubyport',
    ckKeyAccess: 'Đã thiết lập cách nhận chìa khóa',
    ckWifi: 'Đã nhập Wi-Fi',
    ckGuideEnabled: 'Đã bật hướng dẫn',
    ckStripe: 'Tài khoản Stripe',

    searchLabel: 'Tìm cài đặt',
    searchPlaceholderKeyboard: 'Tìm cài đặt… (nhấn / )',
    searchPlaceholderTouch: 'Tìm cài đặt… ví dụ wifi, đặt cọc',
    searchResults: 'Kết quả tìm kiếm',
    searchEmpty: 'Không tìm thấy — hãy thử từ khác',

    barRegion: 'Lưu thay đổi',
    barUnsaved: (g) => `Thay đổi chưa lưu: ${g}`,
    barClean: 'Không có thay đổi chưa lưu',
    barDiscard: 'Hủy thay đổi',
    barSave: 'Lưu',
    barGuideSaving: 'Đang lưu hướng dẫn…',

    dangerZone: 'Vùng nguy hiểm',
    checkinLinkTitle: 'Check-in của khách',

    advanced: 'Cài đặt nâng cao',
    advancedLock: 'Nâng cao: thao tác với khóa',
    chipOn: 'Bật',
    chipOff: 'Tắt',
    chipOnDemand: 'Theo yêu cầu',
    chipOnline: 'Thu trực tuyến',
    cardUnsaved: 'Thay đổi chưa lưu',
    sumCityTax: (r, o) => `${r} CZK / người lớn / đêm · thu trực tuyến ${o ? 'bật' : 'tắt'}`,
    sumDeposit: (e, a) => (e == null ? '…' : e ? (a ? `${a} · giữ trên thẻ trước ngày đến` : 'Bật') : 'Tắt'),
    sumUpsell: (h, l) => {
      if (h === undefined || l === undefined) return '…';
      const name = (m: string) => (m === 'AUTO' ? 'tự động' : 'theo yêu cầu');
      const parts = [h && h !== 'OFF' ? `Giờ thêm: ${name(h)}` : null, l && l !== 'OFF' ? `Hành lý: ${name(l)}` : null].filter(Boolean);
      return parts.length ? parts.join(' · ') : 'Tắt';
    },
    sumDorucenky: 'ZIP biên nhận PDF cho khoảng thời gian đã chọn',
    sumExport: 'CSV và PDF cho khoảng thời gian đã chọn',

    guideHelpOn: 'Nội dung đã sẵn sàng cho khách.',
    guideHelpOff: 'Nội dung vẫn được lưu nhưng khách không nhìn thấy.',
    ggArrival: 'Đến nơi & địa chỉ',
    ggAccess: 'Vào nhà & chìa khóa',
    ggStay: 'Trong kỳ lưu trú',
    ggContent: 'Hướng dẫn, ảnh & liên kết',
    ggDeparture: 'Trả phòng',
    gsHidden: 'Ẩn với khách',
    gsNoAddress: 'Chưa nhập địa chỉ',
    gsArrDep: (a, i, o) => `${a} · đến ${i || '—'} / đi ${o || '—'}`,
    gsCodeMissing: 'thiếu mã',
    gsLockMissing: 'chưa chọn khóa',
    gsWifiMissing: 'thiếu Wi-Fi',
    gsNothingShown: 'Tất cả đều ẩn với khách',
    gsOptional: 'Thông tin tùy chọn · thêm khi cần',
    gsDeparture: (time, f) => `Trả phòng trước ${time || '—'} · ${f ? 'đã nhập hướng dẫn' : 'thêm hướng dẫn'}`,
  },
  uk: {
    tabsAria: 'Групи налаштувань',
    groupBasic: 'Основне',
    groupReservations: 'Бронювання',
    groupArrival: 'Заселення та путівник',
    groupMoney: 'Гроші',
    groupAuthorities: 'Органи та експорт',
    shortBasic: 'Основне',
    shortReservations: 'Бронь',
    shortArrival: 'Заїзд',
    shortMoney: 'Гроші',
    shortAuthorities: 'Органи',
    tabNeedsAttention: 'потребує уваги',
    tabUnsaved: 'є незбережені зміни',

    checklistRegion: 'Налаштування апартаментів',
    checklistProgress: (d, t) => `Налаштування: ${d} з ${t} готово`,
    checklistAllDone: 'Усе налаштовано',
    checklistLoading: 'Завантажую стан налаштувань…',
    checklistShow: 'Показати',
    checklistHide: 'Сховати',
    checklistAriaProgress: (d, t) => `Налаштування: ${d} з ${t} готово`,
    checklistAriaAllDone: 'Налаштування: усе готово',
    checklistAriaLoading: 'Налаштування: завантаження',
    checklistHint: 'Список базується на збережених значеннях — незбережена зміна враховується лише після натискання «Зберегти».',
    statusDone: 'Готово',
    statusTodo: 'Заповнити ›',
    statusNa: 'Не потрібно',
    ckCalendar: 'Календар підключено',
    ckUbyportLogin: 'Дані входу Ubyport заповнено',
    ckUbyportAuto: 'Автоматичне надсилання в Ubyport',
    ckKeyAccess: 'Доступ до ключів налаштовано',
    ckWifi: 'Wi-Fi заповнено',
    ckGuideEnabled: 'Путівник увімкнено',
    ckStripe: 'Рахунок Stripe',

    searchLabel: 'Знайти налаштування',
    searchPlaceholderKeyboard: 'Знайти налаштування… (натисніть / )',
    searchPlaceholderTouch: 'Знайти налаштування… напр. wifi, застава',
    searchResults: 'Результати пошуку',
    searchEmpty: 'Нічого не знайдено — спробуйте інше слово',

    barRegion: 'Збереження змін',
    barUnsaved: (g) => `Незбережені зміни: ${g}`,
    barClean: 'Немає незбережених змін',
    barDiscard: 'Скасувати зміни',
    barSave: 'Зберегти',
    barGuideSaving: 'Зберігаю путівник…',

    dangerZone: 'Небезпечна зона',
    checkinLinkTitle: 'Check-in гостей',

    advanced: 'Додаткові налаштування',
    advancedLock: 'Додатково: операції із замком',
    chipOn: 'Увімкнено',
    chipOff: 'Вимкнено',
    chipOnDemand: 'На вимогу',
    chipOnline: 'Онлайн-збір',
    cardUnsaved: 'Незбережені зміни',
    sumCityTax: (r, o) => `${r} CZK / дорослий / ніч · онлайн-збір ${o ? 'увімкнено' : 'вимкнено'}`,
    sumDeposit: (e, a) => (e == null ? '…' : e ? (a ? `${a} · резервується на картці перед заїздом` : 'Увімкнено') : 'Вимкнено'),
    sumUpsell: (h, l) => {
      if (h === undefined || l === undefined) return '…';
      const name = (m: string) => (m === 'AUTO' ? 'автоматично' : 'за запитом');
      const parts = [h && h !== 'OFF' ? `Додаткові години: ${name(h)}` : null, l && l !== 'OFF' ? `Багаж: ${name(l)}` : null].filter(Boolean);
      return parts.length ? parts.join(' · ') : 'Вимкнено';
    },
    sumDorucenky: 'ZIP з PDF-квитанціями за обраний період',
    sumExport: 'CSV і PDF за обраний період',

    guideHelpOn: 'Вміст готовий для гостей.',
    guideHelpOff: 'Вміст зберігається, але гості його не бачать.',
    ggArrival: 'Заїзд і адреса',
    ggAccess: 'Доступ і ключі',
    ggStay: 'Під час проживання',
    ggContent: 'Інструкції, фото й посилання',
    ggDeparture: 'Виїзд',
    gsHidden: 'Приховано від гостей',
    gsNoAddress: 'Адресу не заповнено',
    gsArrDep: (a, i, o) => `${a} · заїзд ${i || '—'} / виїзд ${o || '—'}`,
    gsCodeMissing: 'немає коду',
    gsLockMissing: 'замок не вибрано',
    gsWifiMissing: 'немає Wi-Fi',
    gsNothingShown: 'Усе приховано від гостей',
    gsOptional: 'Необов’язкова інформація · додайте за потреби',
    gsDeparture: (time, f) => `Виїзд до ${time || '—'} · ${f ? 'інструкції заповнено' : 'додати інструкції'}`,
  },
};
